/* Loomish Voices - the filter, the rail, and nothing else.
 *
 * The section renders every card server-side, so the library is in the HTML
 * whether this file runs or not: it is indexable, it reads without JavaScript,
 * and if this fails the visitor still gets all the reviews and a rail they can
 * scroll. Everything here is an improvement on a page that already works.
 *
 * Filtering hides cards rather than rebuilding them. At the sizes this section
 * will ever hold that is instant, and it keeps one copy of a card's markup in
 * one place - the Liquid snippet - instead of a second copy written in JS that
 * quietly drifts from it.
 */
(function () {
  'use strict';

  var REDUCE = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function Voices(root) {
    if (root.dataset.lvMounted === '1') return;
    root.dataset.lvMounted = '1';

    var self = this;
    this.root = root;
    this.rail = root.querySelector('[data-lv-rail]');
    this.cards = Array.prototype.slice.call(root.querySelectorAll('[data-lv-card]'));
    this.tabs = Array.prototype.slice.call(root.querySelectorAll('[data-lv-tab]'));
    this.bar = root.querySelector('[data-lv-bar]');
    this.wrap = root.querySelector('.lv-railwrap');
    this.prev = root.querySelector('[data-lv-prev]');
    this.next = root.querySelector('[data-lv-next]');
    this.empty = root.querySelector('[data-lv-empty]');

    this.counts = { all: 0, b2b: 0, b2c: 0 };
    this.cards.forEach(function (c) {
      var a = c.getAttribute('data-audience') || 'b2b';
      self.counts.all++;
      if (self.counts[a] !== undefined) self.counts[a]++;
    });

    this.wireTabs();
    this.wireRail();
    this.wireCounters();

    /* The starting audience is set BEFORE the section is allowed to appear.
       Where motion is declined the reveal is instant, so doing it the other
       way round would show the wrong eyebrow for a frame and then correct
       itself, which reads as a fault rather than as a filter. */
    this.show(root.dataset.default || 'all', true);
    this.reveal();
  }

  /* ------------------------------------------------------------- entrance */
  /*
   * A focus pull, driven by one class. No per-element script, no layout read
   * per frame - the stagger is a CSS custom property the section already set.
   */
  Voices.prototype.reveal = function () {
    var root = this.root;
    if (REDUCE || !('IntersectionObserver' in window)) {
      root.classList.add('is-in');
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) {
        root.classList.add('is-in');
        io.disconnect();
      }
    }, { threshold: 0, rootMargin: '0px 0px -12% 0px' });
    io.observe(root);
  };

  /* --------------------------------------------------------------- filter */
  Voices.prototype.wireTabs = function () {
    var self = this;
    this.tabs.forEach(function (t) {
      t.addEventListener('click', function () {
        self.show(t.getAttribute('data-lv-tab'), false);
      });
    });

    if (this.root.dataset.counts === '1') {
      ['all', 'b2b', 'b2c'].forEach(function (k) {
        var el = self.root.querySelector('[data-lv-count="' + k + '"]');
        if (el) el.textContent = self.counts[k];
      });
    }
  };

  /**
   * Swap the audience.
   *
   * The cards fade out, change, and fade back in on the far side of one frame,
   * so the visitor never sees the set rearrange itself. `first` skips the fade
   * entirely — an animation on page load is not a transition, it is a flicker.
   */
  Voices.prototype.show = function (aud, first) {
    var self = this;
    if (!aud) aud = 'all';
    if (this.audience === aud) return;
    this.audience = aud;

    this.tabs.forEach(function (t) {
      var on = t.getAttribute('data-lv-tab') === aud;
      t.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    var apply = function () {
      var shown = 0;
      self.cards.forEach(function (c) {
        var a = c.getAttribute('data-audience') || 'b2b';
        var on = aud === 'all' || a === aud;
        c.hidden = !on;
        if (on) shown++;
      });

      /* An empty filter says so. A section that renders an empty stage and no
         explanation is indistinguishable from a broken one. */
      /* The empty note is shown by .is-empty alone. Setting `hidden` on it as
         well would be a second switch for one thing, and the two disagree:
         the class rule outranks the hidden attribute. */
      self.root.classList.toggle('is-empty', shown === 0);

      self.swapText(aud);
      if (self.rail) self.rail.scrollLeft = 0;
      self.measure();
    };

    if (first || REDUCE) { apply(); return; }

    this.root.classList.add('is-swapping');
    window.setTimeout(function () {
      apply();
      /* Two frames: one for the hidden cards to leave the layout, one for the
         browser to read the new state as the thing being transitioned TO. */
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          self.root.classList.remove('is-swapping');
        });
      });
    }, 200);
  };

  /** The eyebrow, the large word and the panel copy all follow the filter. */
  Voices.prototype.swapText = function (aud) {
    var sel = '[data-lv-eyebrow],[data-lv-panel-eyebrow],[data-lv-word],[data-lv-phead],[data-lv-pbody]';
    var nodes = this.root.querySelectorAll(sel);
    for (var i = 0; i < nodes.length; i++) {
      var v = nodes[i].getAttribute('data-' + aud);
      if (v !== null && v !== '') nodes[i].textContent = v;
    }
  };

  /* ----------------------------------------------------------------- rail */
  Voices.prototype.wireRail = function () {
    var self = this;
    var rail = this.rail;
    if (!rail) return;

    rail.addEventListener('scroll', function () { self.measure(); }, { passive: true });
    window.addEventListener('resize', function () { self.measure(); }, { passive: true });

    if (this.prev) this.prev.addEventListener('click', function () { self.step(-1); });
    if (this.next) this.next.addEventListener('click', function () { self.step(1); });

    /*
     * Keyboard. The rail is focusable and a native scroll container, so arrows
     * already work — but a page of cards should move a card at a time, not a
     * few pixels, and Home/End should reach the ends.
     */
    rail.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); self.step(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); self.step(-1); }
      else if (e.key === 'Home') { e.preventDefault(); rail.scrollTo({ left: 0, behavior: REDUCE ? 'auto' : 'smooth' }); }
      else if (e.key === 'End') { e.preventDefault(); rail.scrollTo({ left: rail.scrollWidth, behavior: REDUCE ? 'auto' : 'smooth' }); }
    });

    if (this.root.dataset.drag === '1') this.wireDrag();
    this.measure();
  };

  /**
   * Mouse drag, and only mouse.
   *
   * Touch already has a better version of this natively, and hijacking it is
   * how carousels end up fighting the page's own vertical scroll. The drag is
   * abandoned the moment it turns out to be a click, so links and text
   * selection inside a card keep working.
   */
  Voices.prototype.wireDrag = function () {
    var rail = this.rail;
    var down = false, moved = false, startX = 0, startLeft = 0, id = null;

    rail.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      down = true; moved = false; id = e.pointerId;
      startX = e.clientX; startLeft = rail.scrollLeft;
    });

    rail.addEventListener('pointermove', function (e) {
      if (!down || e.pointerId !== id) return;
      var dx = e.clientX - startX;
      if (!moved && Math.abs(dx) < 4) return;
      if (!moved) {
        moved = true;
        rail.classList.add('is-drag');
        /* Capture only once it is genuinely a drag, so a plain click on a
           card is never swallowed. */
        try { rail.setPointerCapture(id); } catch (err) {}
      }
      e.preventDefault();
      rail.scrollLeft = startLeft - dx;
    });

    var end = function (e) {
      if (!down || (e && e.pointerId !== id)) return;
      down = false;
      if (moved) {
        try { rail.releasePointerCapture(id); } catch (err) {}
        /*
         * Swallow the click this drag would otherwise fire on a card - and
         * take the listener off again on the next tick whether that click
         * arrives or not. Left armed, a drag that ended outside the rail
         * would eat the visitor's NEXT click instead.
         */
        var kill = function (ev) { ev.stopPropagation(); ev.preventDefault(); off(); };
        var off = function () { rail.removeEventListener('click', kill, true); };
        rail.addEventListener('click', kill, true);
        window.setTimeout(off, 0);
      }
      rail.classList.remove('is-drag');
      moved = false; id = null;
    };

    rail.addEventListener('pointerup', end);
    rail.addEventListener('pointercancel', end);
    rail.addEventListener('pointerleave', end);
    rail.addEventListener('dragstart', function (e) { if (moved) e.preventDefault(); });
  };

  /** One card, plus the gap. */
  Voices.prototype.step = function (dir) {
    var rail = this.rail;
    if (!rail) return;
    var card = rail.querySelector('[data-lv-card]:not([hidden])');
    var w = card ? card.getBoundingClientRect().width : rail.clientWidth * 0.8;
    var gap = parseFloat(getComputedStyle(rail).columnGap || getComputedStyle(rail).gap || 18) || 18;
    rail.scrollTo({
      left: rail.scrollLeft + dir * (w + gap),
      behavior: REDUCE ? 'auto' : 'smooth'
    });
  };

  /** Progress rule, edge fades and arrow states, from one layout read. */
  Voices.prototype.measure = function () {
    var self = this;
    if (this._tick) return;
    this._tick = true;
    window.requestAnimationFrame(function () {
      self._tick = false;
      var rail = self.rail;
      if (!rail) return;

      var max = rail.scrollWidth - rail.clientWidth;
      var atStart = rail.scrollLeft <= 1;
      var atEnd = max <= 1 || rail.scrollLeft >= max - 1;

      if (self.bar) {
        var frac = rail.scrollWidth > 0 ? rail.clientWidth / rail.scrollWidth : 1;
        if (frac > 1) frac = 1;
        var prog = max > 0 ? rail.scrollLeft / max : 0;
        self.bar.style.width = (frac * 100) + '%';
        self.bar.style.transform = 'translateX(' + (prog * (100 / frac - 100)) + '%)';
      }

      if (self.wrap) {
        self.wrap.classList.toggle('can-prev', !atStart);
        self.wrap.classList.toggle('can-next', !atEnd);
      }
      if (self.prev) self.prev.disabled = atStart;
      if (self.next) self.next.disabled = atEnd;
    });
  };

  /* -------------------------------------------------------------- counter */
  /*
   * Kept from the reference component's one genuinely reusable idea: count up
   * once, when it is actually on screen. It only ever runs on figures the shop
   * has supplied, because the section does not show figures by default.
   */
  Voices.prototype.wireCounters = function () {
    var nodes = this.root.querySelectorAll('[data-lv-counter]');
    if (!nodes.length) return;

    Array.prototype.forEach.call(nodes, function (el) {
      var to = parseFloat(el.getAttribute('data-to'));
      var dec = parseInt(el.getAttribute('data-dec') || '0', 10);
      if (isNaN(to)) return;

      if (REDUCE || !('IntersectionObserver' in window)) {
        el.textContent = to.toFixed(dec);
        return;
      }

      el.textContent = (0).toFixed(dec);
      var io = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        var t0 = 0, dur = 1100;
        var step = function (now) {
          if (!t0) t0 = now;
          var t = Math.min(1, (now - t0) / dur);
          var e = 1 - Math.pow(1 - t, 3);   /* easeOut, no overshoot */
          el.textContent = (to * e).toFixed(dec);
          if (t < 1) window.requestAnimationFrame(step);
        };
        window.requestAnimationFrame(step);
      }, { threshold: 0.4 });
      io.observe(el);
    });
  };

  /* ------------------------------------------------------------------ boot */
  function boot(scope) {
    var nodes = (scope || document).querySelectorAll('[data-lv]');
    for (var i = 0; i < nodes.length; i++) {
      try { new Voices(nodes[i]); } catch (e) {
        if (window.console) window.console.error('[loomish-voices]', e);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  /* The Theme Editor re-renders a section in place. */
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
